import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import {
  Alert, Button, Card, Modal, Spin, Table, Typography,
} from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import {
  Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { useAccuracyQuery, usePlanQuery, useSetBlend } from '../hooks';
import * as fmt from '../format';

const { Text } = Typography;

const SOLD = '#262626';
const PREDICTED = '#1677ff';
const HORIZON_LABEL = { 1: '1 mo', 2: '2 mo', 3: '3 mo' };

const monthYear = (v) => (v ? dayjs(v).format('MMM YYYY') : '—');

const pct = (v) => (v == null ? '—' : `${v}%`);

const quarterAccuracy = (wape) => (
  wape == null ? null : Math.round((100 - wape) * 10) / 10
);

const directionText = (v) => {
  if (v == null) return '—';
  if (v > 0) return `${v}% ↑`;
  if (v < 0) return `${Math.abs(v)}% ↓`;
  return '0%';
};

const Direction = ({ value }) => {
  if (value == null) return '—';
  let arrow = null;
  if (value > 0) arrow = <ArrowUpOutlined />;
  else if (value < 0) arrow = <ArrowDownOutlined />;
  const label = value === 0 ? '0%' : `${Math.abs(value)}%`;
  return (
    <span className="forecasting-bias">
      {label}
      {arrow}
    </span>
  );
};

Direction.propTypes = {
  value: PropTypes.number,
};

Direction.defaultProps = {
  value: null,
};

const yDomain = (points) => {
  const values = points.flatMap((d) => [d.sold, d.predicted]).filter((v) => v != null);
  if (!values.length) return [0, 1];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min;
  const pad = span > 0 ? span * 0.4 : Math.max(max * 0.04, 1);
  return [min - pad, max + pad];
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="forecasting-accuracy-tooltip">
      <div className="forecasting-accuracy-tooltip-title">{label}</div>
      {payload.map((item) => (
        <div key={item.dataKey} className="forecasting-accuracy-tooltip-row">
          <span>{item.name}</span>
          <span>{fmt.litres(item.value)}</span>
        </div>
      ))}
    </div>
  );
};

ChartTooltip.propTypes = {
  active: PropTypes.bool,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  payload: PropTypes.arrayOf(PropTypes.shape({
    dataKey: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.number,
    color: PropTypes.string,
  })),
};

ChartTooltip.defaultProps = {
  active: false,
  label: '',
  payload: [],
};

const QuarterChart = ({ months }) => {
  const data = months.map((m) => ({
    label: HORIZON_LABEL[m.h] || `${m.h} mo`,
    sold: m.actualLitres,
    predicted: m.forecastLitres,
  }));
  if (!data.length) return '—';
  return (
    <div className="forecasting-accuracy-spark">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{
            top: 6,
            right: 12,
            bottom: 0,
            left: 12,
          }}
        >
          <XAxis
            dataKey="label"
            tick={{ fontSize: 10 }}
            interval={0}
            axisLine={false}
            tickLine={false}
            padding={{
              left: 8,
              right: 8,
            }}
          />
          <YAxis hide domain={yDomain(data)} />
          <Tooltip
            content={<ChartTooltip />}
            isAnimationActive={false}
            allowEscapeViewBox={{
              x: true,
              y: true,
            }}
            wrapperStyle={{
              zIndex: 20,
              outline: 'none',
            }}
            cursor={{ stroke: '#d9d9d9' }}
          />
          <Line
            type="monotone"
            dataKey="sold"
            name="Sold"
            stroke={SOLD}
            strokeWidth={1.5}
            dot={{
              r: 2,
            }}
            isAnimationActive={false}
          />
          <Line
            type="monotone"
            dataKey="predicted"
            name="Predicted"
            stroke={PREDICTED}
            strokeWidth={1.5}
            dot={{
              r: 2,
            }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

QuarterChart.propTypes = {
  months: PropTypes.arrayOf(PropTypes.shape({
    h: PropTypes.number,
    actualLitres: PropTypes.number,
    forecastLitres: PropTypes.number,
  })),
};

QuarterChart.defaultProps = {
  months: [],
};

const heat = (t) => {
  const hue = Math.round(Math.min(1, Math.max(0, t)) * 120);
  return {
    backgroundColor: `hsl(${hue}, 72%, 92%)`,
    color: `hsl(${hue}, 62%, 26%)`,
  };
};

const colorScale = (values, higherIsBetter) => {
  const nums = values.filter((v) => v != null && !Number.isNaN(v));
  if (!nums.length) return () => undefined;
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  return (v) => {
    if (v == null || Number.isNaN(v)) return undefined;
    if (max === min) return heat(1);
    const span = higherIsBetter ? v - min : max - v;
    return heat(span / (max - min));
  };
};

// Each beer: accuracy is red at 80% and below. Bias is red at 15% either side.
const fixedScale = (v, redAt, greenAt) => {
  if (v == null || Number.isNaN(v)) return undefined;
  return heat((v - redAt) / (greenAt - redAt));
};

const beerAccuracyStyle = (accuracy) => fixedScale(accuracy, 80, 100);
const beerBiasStyle = (absBias) => fixedScale(absBias, 15, 0);

const AccuracyTab = () => {
  const accuracy = useAccuracyQuery();
  const plan = usePlanQuery();
  const setBlend = useSetBlend();
  const canEdit = !!plan.data?.canEdit;
  const { data } = accuracy;
  const blends = data?.blends || [];
  const weightPercent = data?.weightPercent;
  const active = blends.find((b) => b.weightPercent === weightPercent);
  const mixAccuracyStyle = useMemo(
    () => colorScale(blends.map((b) => quarterAccuracy(b.wape3m)), true),
    [blends],
  );
  const mixBiasStyle = useMemo(
    () => colorScale(
      blends.map((b) => (b.bias3m == null ? null : Math.abs(b.bias3m))),
      false,
    ),
    [blends],
  );

  const columns = useMemo(() => {
    const confirm = (next) => {
      Modal.confirm({
        title: `Brew from ${next}% last year?`,
        content: 'Rebuilds brew-plan litres on this mix. Does not retrain the forecast.',
        okText: 'Use this mix',
        onOk: () => setBlend.mutateAsync(next),
      });
    };
    return [
      { title: '', dataIndex: 'label', width: 160 },
      ...blends.map((blend) => {
        const key = String(blend.weightPercent);
        const inUse = blend.weightPercent === weightPercent;
        return {
          title: (
            <div className="forecasting-blend-col">
              <div>{`${blend.weightPercent}%`}</div>
              {inUse && <Text type="secondary">In use</Text>}
              {canEdit && !inUse && (
                <Button
                  type="link"
                  size="small"
                  disabled={setBlend.isLoading}
                  onClick={() => confirm(blend.weightPercent)}
                >
                  Use
                </Button>
              )}
            </div>
          ),
          dataIndex: key,
          align: 'right',
          render: (value, row) => (
            row.key === 'direction' ? <Direction value={value} /> : pct(value)
          ),
          onCell: (row) => {
            const value = row[key];
            const style = row.key === 'direction'
              ? mixBiasStyle(value == null ? null : Math.abs(value))
              : mixAccuracyStyle(value);
            return { style };
          },
        };
      }),
    ];
  }, [blends, canEdit, mixAccuracyStyle, mixBiasStyle, setBlend, weightPercent]);

  if (accuracy.isLoading) return <Spin />;
  if (accuracy.isError) return <Alert type="error" message="Could not load accuracy" />;

  const { cutoffs } = data;
  const cutoffLine = cutoffs && cutoffs.nOrigins
    ? `${fmt.number(cutoffs.nOrigins)} monthly cutoffs, ${monthYear(cutoffs.first)}`
      + ` – ${monthYear(cutoffs.last)}, forecasting 1–3 months ahead.`
    : null;
  const accuracyLabel = active ? pct(quarterAccuracy(active.wape3m)) : null;
  const headline = active
    ? `Forecast is on ${weightPercent}% last year. ${accuracyLabel} accurate over 3 months, `
      + `${directionText(active.bias3m)}.`
    : `Forecast is on ${weightPercent}% last year.`;
  const rows = [
    {
      key: 'miss',
      label: 'Quarter accuracy',
      ...Object.fromEntries(
        blends.map((b) => [String(b.weightPercent), quarterAccuracy(b.wape3m)]),
      ),
    },
    {
      key: 'direction',
      label: 'Too high or too low',
      ...Object.fromEntries(blends.map((b) => [String(b.weightPercent), b.bias3m])),
    },
  ];
  const beers = active ? active.beers : [];
  const beerColumns = [
    { title: 'Beer', dataIndex: 'beerName' },
    {
      title: 'Sold vs predicted',
      dataIndex: 'months',
      width: 344,
      onCell: () => ({ className: 'forecasting-accuracy-cell' }),
      render: (months) => <QuarterChart months={months} />,
    },
    {
      title: 'Quarter accuracy',
      dataIndex: 'wape3m',
      align: 'right',
      onCell: (row) => ({ style: beerAccuracyStyle(quarterAccuracy(row.wape3m)) }),
      render: (v) => pct(quarterAccuracy(v)),
    },
    {
      title: 'Too high or too low',
      dataIndex: 'bias3m',
      align: 'right',
      onCell: (row) => ({
        style: beerBiasStyle(row.bias3m == null ? null : Math.abs(row.bias3m)),
      }),
      render: (v) => <Direction value={v} />,
    },
  ];

  return (
    <div>
      <Text>{headline}</Text>
      {cutoffLine && (
        <>
          <br />
          <Text type="secondary">{cutoffLine}</Text>
        </>
      )}
      {!blends.length && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message="No backtest scores yet. Run the forecast first."
        />
      )}
      {!!blends.length && !active && (
        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message="This mix sits between the 10% steps, so it is not marked on the grid."
        />
      )}
      <Card className="forecasting-section" title="Mix" style={{ marginTop: 12 }}>
        <Table
          className="forecasting-blend-table"
          rowKey="key"
          size="small"
          pagination={false}
          loading={setBlend.isLoading}
          dataSource={rows}
          columns={columns}
          scroll={{ x: true }}
        />
      </Card>
      <Card className="forecasting-section" title="Each beer">
        <Table
          className="forecasting-accuracy-table"
          rowKey="beerName"
          size="small"
          pagination={false}
          dataSource={beers}
          columns={beerColumns}
        />
      </Card>
    </div>
  );
};

export default AccuracyTab;
